import type {
	AnsweredQuestion,
	AnyQuestion,
	Country,
	ProductSigningStatus,
	SniCode,
} from "@sveabank/cc-common";
import { onboardingApi } from ".";
import type { InactiveEngagement } from "../types/application";
import type {
	ClcAddonConfig,
	ClcApplication,
	ClcApplicationDetails,
	ClcBeneficialOwnerAnswerSet,
	ClcBeneficialOwnerControlTypeMap,
	ClcBeneficialOwnerQuestionSet,
	ClcBeneficialOwnership,
	ClcCardConfig,
	ClcCompanyRegistrationForm,
	ClcCompanyShares,
	ClcCompanyStructureInput,
	ClcDebitCardConfigForm,
	ClcPaymentConfig,
	ClcPaymentOrder,
	ClcPerson,
	ClcProductQuestionSet,
	ClcRole,
	ClcSignatoryCombination,
} from "../types/clc-application";
import type { ClcPackage, SelectedClcPackage, SelectedPackage } from "../types/package";
import type { Municipality } from "../types/shared";
import type { SigningStatus } from "../types/signing";
import {
	cancelClcSign,
	configureClcCard,
	confirmClcProductConfig,
	createClcPaymentOrder,
	deleteClcApplication,
	fetchClcAgreement,
	fetchClcAmlQuestions,
	fetchClcApplicationDetails,
	fetchClcApplicationQuestions,
	fetchClcApplicationRevision,
	fetchClcApplications,
	fetchClcBeneficialOwnerQuestions,
	fetchClcCards,
	fetchClcControlTypes,
	fetchClcCountries,
	getAvailableClcPackages,
	fetchClcPersonData,
	fetchClcPossibleOrganisations,
	fetchClcRoles,
	fetchClcSelectedPackage,
	fetchClcSignatoryCombinations,
	fetchClcSigningStatus,
	fetchClcSniCodes,
	fetchMunicipalities,
	initClcApplication,
	initializeClcAgreementSigning,
	postClcAmlAnswers,
	postClcApplication,
	postClcApplicationQuestions,
	postClcBeneficialOwnerAnswers,
	postClcBeneficialOwnership,
	postClcCompanyRegistration,
	postClcCompanyShares,
	postClcRepresentatives,
	removeClcAddons,
	selectClcAddon,
	setClcOrganisationNumber,
	setupClcAgreement,
	selectClcPackage,
	unSelectClcPackage,
} from "./endpoints/clc-application";

export const clcApplicationsApiSlice = onboardingApi.injectEndpoints({
	endpoints: (builder) => ({
		fetchClcApplications: builder.query<ClcApplication[], void>({
			query: () => fetchClcApplications(),
			providesTags: ["ClcApplications", "CurrentClcApplication"],
			transformResponse: (response: { data: ClcApplication[] }) =>
				response.data.filter(
					(application) =>
						application.clcStatus !== "Finalized" && application.clcStatus !== "Cancelled",
				),
			extraOptions: { withAuth: true },
		}),

		initClcApplication: builder.mutation<
			{ revision?: string; applicationId: string },
			{ applicationId: string }
		>({
			query: ({ applicationId }) => initClcApplication(applicationId),
			invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
			transformResponse: (_returnValue, _meta, arg) => ({
				applicationId: arg.applicationId,
			}),
			extraOptions: { withAuth: true },
		}),

		fetchClcApplicationDetails: builder.query<ClcApplicationDetails, string>({
			query: (applicationId) => fetchClcApplicationDetails(applicationId),
			transformResponse: (response: { data: ClcApplicationDetails }) => response.data,
			extraOptions: { withAuth: true },
			providesTags: ["CurrentClcApplication"],
		}),

		fetchClcApplicationRevision: builder.query<void, string>({
			query: (applicationId) => fetchClcApplicationRevision(applicationId),
			forceRefetch: () => true,
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),

		fetchMunicipalities: builder.query<Array<Municipality>, void>({
			query: () => fetchMunicipalities(),
			transformResponse: (response: { data: Array<Municipality> }) => response.data,
			extraOptions: { withAuth: true },
		}),

		deleteClcApplication: builder.mutation<void, string>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
			query: (applicationId) => deleteClcApplication(applicationId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),

		postClcCompanyRegistration: builder.mutation<
			void,
			{ applicationId: string; companyInfo: ClcCompanyRegistrationForm }
		>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcApplicationQuestions"],
			query: ({ applicationId, companyInfo }) =>
				postClcCompanyRegistration(applicationId, companyInfo),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),

		postClcCompanyShares: builder.mutation<
			void,
			{ applicationId: string; shares: ClcCompanyShares }
		>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcCards"],
			query: ({ applicationId, shares }) => postClcCompanyShares(applicationId, shares),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),

		postClcBeneficialOwnership: builder.mutation<
			void,
			{ applicationId: string; beneficialOwners: ClcBeneficialOwnership }
		>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "BOQuestions", "ClcCards"],
			query: ({ applicationId, beneficialOwners }) =>
				postClcBeneficialOwnership(applicationId, beneficialOwners),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		postClcRepresentatives: builder.mutation<
			void,
			{ applicationId: string; representatives: ClcCompanyStructureInput }
		>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcCards"],
			query: ({ applicationId, representatives }) =>
				postClcRepresentatives(applicationId, representatives),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		fetchClcSniCodes: builder.query<Array<SniCode>, void>({
			query: () => fetchClcSniCodes(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<SniCode> }) => response.data,
		}),

		fetchClcPersonData: builder.query<ClcPerson, { applicationId: string; nationalId: string }>({
			query: ({ applicationId, nationalId }) => fetchClcPersonData(applicationId, nationalId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: ClcPerson }) => response.data,
		}),

		fetchClcBeneficialOwnerQuestions: builder.query<Array<ClcBeneficialOwnerQuestionSet>, string>({
			providesTags: ["BOQuestions"],
			query: (applicationId) => fetchClcBeneficialOwnerQuestions(applicationId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<ClcBeneficialOwnerQuestionSet> }) =>
				response.data,
		}),

		fetchClcControlTypes: builder.query<ClcBeneficialOwnerControlTypeMap, void>({
			query: () => fetchClcControlTypes(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: ClcBeneficialOwnerControlTypeMap }) => response.data,
		}),
		fetchClcAmlQuestions: builder.query<Array<ClcProductQuestionSet>, string>({
			query: (applicationId) => fetchClcAmlQuestions(applicationId),
			transformResponse: (response: { data: Array<ClcProductQuestionSet> }) => response.data,
			providesTags: ["ClcAmlQuestions"],
			extraOptions: { withAuth: true },
		}),
		postClcAmlAnswers: builder.mutation<
			null,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
				productId: string;
			}
		>({
			query: ({ applicationId, productId, questions }) =>
				postClcAmlAnswers(applicationId, productId, questions),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcAmlQuestions"],
		}),
		postClcApplicationQuestions: builder.mutation<
			null,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
			}
		>({
			query: ({ applicationId, questions }) =>
				postClcApplicationQuestions(applicationId, questions),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcApplicationQuestions"],
		}),
		setupClcAgreement: builder.mutation<void, string>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
			query: (applicationId) => setupClcAgreement(applicationId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		fetchClcAgreement: builder.query<string, string>({
			query: (applicationId) => fetchClcAgreement(applicationId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { document: string }) => response.document,
		}),
		fetchClcPackages: builder.query<ClcPackage[], string>({
			query: (applicationId) => getAvailableClcPackages(applicationId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: ClcPackage[] }) => response.data,
		}),
		selectClcPackage: builder.mutation<void, { applicationId: string; packageId: string }>({
			invalidatesTags: ["ClcPackage", "ClcCards"],
			query: ({ applicationId, packageId }) => selectClcPackage(applicationId, packageId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		unSelectClcPackage: builder.mutation<void, string>({
			query: (applicationId) => unSelectClcPackage(applicationId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		fetchClcPossibleOrganisations: builder.query<Array<InactiveEngagement>, string>({
			query: (applicationId) => fetchClcPossibleOrganisations(applicationId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<InactiveEngagement> }) => response.data,
		}),
		fetchClcCountries: builder.query<Array<Country>, void>({
			query: () => fetchClcCountries(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: Array<Country> }) => response.data,
		}),
		initializeClcAgreementSigning: builder.mutation<
			ProductSigningStatus,
			{
				applicationId: string;
			}
		>({
			query: ({ applicationId }) => initializeClcAgreementSigning(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
			invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
		}),
		postClcBeneficialOwnerAnswers: builder.mutation<
			null,
			{
				questions: Array<AnsweredQuestion>;
				applicationId: string;
				nationalId: string;
				additional: Array<ClcBeneficialOwnerAnswerSet> | null;
			}
		>({
			query: ({ questions, applicationId, nationalId, additional }) =>
				postClcBeneficialOwnerAnswers(applicationId, nationalId, questions, additional),
			extraOptions: {
				revision: true,
				allowRevisionConflict: false,
				withAuth: true,
			},
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcAmlQuestions"],
		}),
		getClcSigningStatus: builder.mutation<
			SigningStatus,
			{
				applicationId: string;
			}
		>({
			query: ({ applicationId }) => fetchClcSigningStatus(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		cancelClcSign: builder.mutation<void, string>({
			query: (applicationId) => cancelClcSign(applicationId),
			extraOptions: {
				revision: true,
				allowRevisionConflict: true,
				withAuth: true,
			},
		}),
		fetchClcRoles: builder.query<ClcRole[], void>({
			query: () => fetchClcRoles(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: ClcRole[] }) => response.data,
		}),
		fetchClcSignatoryCombinations: builder.query<ClcSignatoryCombination[], void>({
			query: () => fetchClcSignatoryCombinations(),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: ClcSignatoryCombination[] }) => response.data,
		}),
		postClcApplication: builder.mutation<void, string>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
			query: (applicationId) => postClcApplication(applicationId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		createClcPaymentOrder: builder.mutation<
			ClcPaymentOrder,
			{ applicationId: string; config: ClcPaymentConfig }
		>({
			// invalidatesTags: ["ClcApplications", "CurrentClcApplication"],
			query: ({ applicationId, config }) => createClcPaymentOrder(applicationId, config),
			transformResponse: (response: ClcPaymentOrder) => response,
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		confirmClcProductConfig: builder.mutation<void, string>({
			invalidatesTags: ["ClcApplications", "CurrentClcApplication", "ClcCards", "ClcPackage"],
			query: (applicationId) => confirmClcProductConfig(applicationId),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		configureClcCard: builder.mutation<
			void,
			{ applicationId: string; cardConfig: ClcDebitCardConfigForm }
		>({
			invalidatesTags: ["ClcCards", "ClcPackage"],
			query: ({ applicationId, cardConfig: config }) => configureClcCard(applicationId, config),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		selectClcAddon: builder.mutation<void, { applicationId: string; addonConfig: ClcAddonConfig }>({
			invalidatesTags: ["ClcCards", "ClcPackage"],
			query: ({ applicationId, addonConfig }) => selectClcAddon(applicationId, addonConfig),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		removeClcAddons: builder.mutation<void, { applicationId: string; addons: Array<string> }>({
			invalidatesTags: ["ClcCards", "ClcPackage"],
			query: ({ applicationId, addons }) => removeClcAddons(applicationId, addons),
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
		fetchClcCards: builder.query<ClcCardConfig, string>({
			query: (applicationId) => fetchClcCards(applicationId),
			extraOptions: { withAuth: true },
			providesTags: ["ClcCards"],
			transformResponse: (response: { data: ClcCardConfig }) => response.data,
		}),
		fetchClcApplicationQuestions: builder.query<Array<AnyQuestion>, string>({
			query: (applicationId) => fetchClcApplicationQuestions(applicationId),
			extraOptions: { withAuth: true },
			providesTags: ["ClcApplicationQuestions"],
			transformResponse: (response: { data: Array<AnyQuestion> }) => response.data,
		}),
		fetchClcSelectedPackage: builder.query<SelectedClcPackage, string>({
			providesTags: ["ClcPackage"],
			query: (applicationId) => fetchClcSelectedPackage(applicationId),
			extraOptions: { withAuth: true },
			transformResponse: (response: { data: SelectedClcPackage }) => response.data,
		}),
		setClcOrganisationNumber: builder.mutation<null, { applicationId: string; orgNr: string }>({
			query: ({ applicationId, orgNr }) => setClcOrganisationNumber(applicationId, orgNr),
			invalidatesTags: ["ClcApplications"],
			extraOptions: {
				revision: true,
				withAuth: true,
			},
		}),
	}),
	overrideExisting: false,
});

export const {
	useInitClcApplicationMutation,
	useFetchClcApplicationsQuery,
	useDeleteClcApplicationMutation,
	useFetchClcApplicationRevisionQuery,
	useFetchClcApplicationDetailsQuery,
	usePostClcCompanyRegistrationMutation,
	usePostClcCompanySharesMutation,
	useFetchClcSniCodesQuery,
	useLazyFetchClcPersonDataQuery,
	useFetchClcBeneficialOwnerQuestionsQuery,
	useFetchClcControlTypesQuery,
	usePostClcBeneficialOwnershipMutation,
	useFetchClcAmlQuestionsQuery,
	usePostClcAmlAnswersMutation,
	usePostClcBeneficialOwnerAnswersMutation,
	useSetupClcAgreementMutation,
	useLazyFetchClcAgreementQuery,
	useInitializeClcAgreementSigningMutation,
	useGetClcSigningStatusMutation,
	useCancelClcSignMutation,
	usePostClcRepresentativesMutation,
	useFetchClcRolesQuery,
	useFetchClcSignatoryCombinationsQuery,
	usePostClcApplicationMutation,
	useCreateClcPaymentOrderMutation,
	useFetchClcCardsQuery,
	useFetchClcPackagesQuery,
	useSelectClcPackageMutation,
	useUnSelectClcPackageMutation,
	useFetchClcSelectedPackageQuery,
	useConfigureClcCardMutation,
	useSelectClcAddonMutation,
	useRemoveClcAddonsMutation,
	useConfirmClcProductConfigMutation,
	useSetClcOrganisationNumberMutation,
	useFetchClcApplicationQuestionsQuery,
	usePostClcApplicationQuestionsMutation,
	useFetchClcCountriesQuery,
	useFetchClcPossibleOrganisationsQuery,
} = clcApplicationsApiSlice;
